import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

export type TaskState = 'Apen' | 'IArbeid' | 'Fullfort' | 'Avbrutt';

export interface TaskView {
  id: string;
  title: string;
  description: string;
  assigneeUserId?: string | null;
  assigneeName?: string | null;
  status: string;
  deadline?: string | null;
  priority?: string | null;
  source: string;
  sourceKind?: string | null;
  sourceId?: string | null;
  checklistTotal: number;
  checklistDone: number;
}

export interface DugnadView {
  id: string;
  title: string;
  start: string;
  end: string;
  location: string;
  signupCount: number;
  published: boolean;
  myStatus?: string | null;
}

export interface DugnadEquipmentView {
  item: string;
  broughtByUserId?: string | null;
}

export interface DugnadTaskView {
  text: string;
  assigneeUserId?: string | null;
  done: boolean;
}

export interface DugnadSignupView {
  userId: string;
  name: string;
  status: string;
}

export interface DugnadDetail {
  id: string;
  title: string;
  description: string;
  start: string;
  end: string;
  location: string;
  equipment: DugnadEquipmentView[];
  tasks: DugnadTaskView[];
  signups: DugnadSignupView[];
  linkedPostId?: string | null;
  myStatus?: string | null;
}

export interface YearWheelView {
  id: string;
  title: string;
  category: string;
  month?: number | null;
  leadDays: number;
  generates: string;
  note: string;
  active: boolean;
  lastGeneratedFor?: string | null;
}

export interface CreateTaskRequest {
  title: string;
  description: string;
  assigneeUserId?: string | null;
  deadline?: string | null;
  priority?: string | null;
}

export interface CreateDugnadRequest {
  title: string;
  description: string;
  start: string;
  end: string;
  location: string;
  equipment: string[];
  tasks: string[];
}

export interface AttendanceEntry {
  userId: string;
  present: boolean;
}

/**
 * Phase V1.7 — board coordination client: the task board, dugnad (board admin + resident sign-up) and
 * the year-wheel. Board writes are gated server-side to styre; residents may view + sign up to dugnad.
 */
@Injectable({ providedIn: 'root' })
export class KoordineringService {
  private readonly http = inject(HttpClient);

  // ── tasks ──
  createTask(req: CreateTaskRequest): Observable<{ id: string }> {
    return this.http.post<{ id: string }>('/api/tasks', req);
  }
  patchTask(id: string, patch: Partial<{ title: string; description: string; assigneeUserId: string; deadline: string; priority: string; status: string }>): Observable<void> {
    return this.http.patch<void>(`/api/tasks/${encodeURIComponent(id)}`, patch);
  }
  // ── dugnad ──
  /** The dugnad as an iCalendar file (`text/calendar`), fetched through HttpClient so the bearer token is attached. */
  dugnadCalendar(id: string): Observable<Blob> {
    return this.http.get(`/api/dugnad/${encodeURIComponent(id)}/calendar`, { responseType: 'blob' });
  }
  createDugnad(req: CreateDugnadRequest): Observable<{ id: string }> {
    return this.http.post<{ id: string }>('/api/dugnad', req);
  }
  signupDugnad(id: string): Observable<void> {
    return this.http.post<void>(`/api/dugnad/${encodeURIComponent(id)}/signup`, {});
  }
  withdrawDugnad(id: string): Observable<void> {
    return this.http.delete<void>(`/api/dugnad/${encodeURIComponent(id)}/signup`);
  }
  registerAttendance(id: string, entries: AttendanceEntry[]): Observable<void> {
    return this.http.post<void>(`/api/dugnad/${encodeURIComponent(id)}/attendance`, { entries });
  }
  publishDugnad(id: string): Observable<{ postId: string }> {
    return this.http.post<{ postId: string }>(`/api/dugnad/${encodeURIComponent(id)}/publish`, {});
  }

  // ── year-wheel ──
}
